/** Demo Mode only - seed data for the Item Rationalisation tabs (Duplicates, Unused Items,
 * Missing Data, Price Anomalies, Suggested Actions) added to the Catalogue Governance screen.
 * Additive to catalogueGovernanceData.ts (that file's queue/status data is unchanged and still
 * powers the Products list badges) - this file only backs the new tabs. No backend
 * persistence exists for any of this yet. */

export interface DuplicateItemGroup {
  groupId: string;
  items: { itemNumber: string; description: string }[];
  suggestedPreferred: string;
}

export const duplicateItemGroups: DuplicateItemGroup[] = [
  {
    groupId: 'dup-1',
    items: [
      { itemNumber: 'TOM-014', description: 'Chopped Tomatoes 2.5kg Tin' },
      { itemNumber: 'TOM-014B', description: 'Tinned Chopped Tomatoes 2.5kg' },
    ],
    suggestedPreferred: 'TOM-014',
  },
  {
    groupId: 'dup-2',
    items: [
      { itemNumber: 'OLV-002', description: 'Extra Virgin Olive Oil 5L' },
      { itemNumber: 'OLV-002X', description: 'Olive Oil Extra Virgin 5 Litre' },
    ],
    suggestedPreferred: 'OLV-002',
  },
];

export interface UnusedItem {
  itemNumber: string;
  description: string;
  lastOrderedDate: string | null;
  usedInRecipe: boolean;
}

export const unusedItems: UnusedItem[] = [
  { itemNumber: 'SPC-045', description: 'Smoked Paprika 500g', lastOrderedDate: '2024-11-02', usedInRecipe: false },
  { itemNumber: 'CRS-210', description: 'Croutons Garlic 1kg', lastOrderedDate: null, usedInRecipe: false },
  { itemNumber: 'BTR-090', description: 'Butter Unsalted Block 250g', lastOrderedDate: '2025-01-15', usedInRecipe: true },
];

export interface MissingDataItem {
  itemNumber: string;
  description: string;
  missingFields: string[];
}

export const missingDataItems: MissingDataItem[] = [
  { itemNumber: 'SPC-009', description: 'Ground Cumin 500g', missingFields: ['Allergens', 'Carbon factor'] },
  { itemNumber: 'FLR-003', description: 'Strong White Bread Flour 16kg', missingFields: ['Nutrition'] },
  { itemNumber: 'DRY-018', description: 'Dried Oregano 200g', missingFields: ['Allergens', 'Nutrition', 'Carbon factor'] },
];

export interface PriceAnomaly {
  itemNumber: string;
  description: string;
  previousPrice: number;
  currentPrice: number;
  changePercent: number;
}

export const priceAnomalies: PriceAnomaly[] = [
  { itemNumber: 'BTR-002', description: 'Salted Butter Block 250g', previousPrice: 2.10, currentPrice: 2.65, changePercent: 26.2 },
  { itemNumber: 'OIL-011', description: 'Sunflower Oil 20L', previousPrice: 18.40, currentPrice: 24.90, changePercent: 35.3 },
];

export type SuggestedActionType = 'Merge' | 'Retire' | 'FlagMissingData' | 'ReplaceInRecipes';

export interface SuggestedAction {
  id: string;
  itemNumber: string;
  description: string;
  action: SuggestedActionType;
  detail: string;
}

export const suggestedActions: SuggestedAction[] = [
  { id: 'act-1', itemNumber: 'TOM-014B', description: 'Tinned Chopped Tomatoes 2.5kg', action: 'Merge', detail: 'Merge into TOM-014 (preferred item)' },
  { id: 'act-2', itemNumber: 'CRS-210', description: 'Croutons Garlic 1kg', action: 'Retire', detail: 'Never ordered, not used in any recipe' },
  { id: 'act-3', itemNumber: 'SPC-009', description: 'Ground Cumin 500g', action: 'FlagMissingData', detail: 'Missing allergen and carbon data' },
  { id: 'act-4', itemNumber: 'OLV-002X', description: 'Olive Oil Extra Virgin 5 Litre', action: 'ReplaceInRecipes', detail: 'Used in 3 recipes - replace with OLV-002' },
];
